import { cloneElement } from 'react';
import React from 'react';

/**
 * Reusable Stat Card — for dashboard metrics
 */
export default function StatCard({
  title,
  value,
  sub,
  icon,
  color = 'bg-slate-100 text-slate-700',
  trend,
  className = '',
  onClick,
  isExpanded = false,
  variant = 'default',
  imageSrc,
  illustration,
  badge,
  trendArrow,
  topColor,
  iconHoverColor,
  iconColor,
  children,
}) {
  if (variant === 'luxury') {
    const borderClasses = topColor
      ? `border-t-[5px] ${topColor}`
      : 'border-t-[5px] border-[#5d6b8b]';

    const textColorClass = iconHoverColor || 'group-hover:text-slate-900';

    return (
      <div
        className={`relative overflow-hidden bg-white ${borderClasses} rounded-2xl flex flex-col transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_35px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 group ${onClick ? 'cursor-pointer' : ''} ${className}`}
        onClick={onClick}
      >
        <div className="flex items-stretch h-24 relative">
          {/* Left Text Content */}
          <div className="flex-1 p-4 flex flex-col justify-center gap-1 z-10">
            <div>
              {badge && (
                <span className="text-[10px] text-slate-400 font-semibold absolute top-4 right-4 z-20">
                  {badge}
                </span>
              )}
              <h3 className="font-body font-normal text-sm text-slate-400 mt-0.5">{title}</h3>
            </div>
            <div>
              <div className="flex items-baseline gap-1 text-slate-500 font-body tracking-tight mt-1">
                <span className="text-2xl font-normal">{value}</span>
                {trendArrow && (
                  <span className="text-green-500 text-lg font-normal ml-1">{trendArrow}</span>
                )}
              </div>
            </div>
          </div>

          {/* Right: Photo OR inline SVG illustration */}
          {imageSrc && (
            <div className="w-[35%] h-full relative overflow-hidden flex-shrink-0">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-white to-transparent z-10 w-8" />
              <img
                src={imageSrc}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 hover:scale-110"
              />
            </div>
          )}
          {illustration && !imageSrc && (
            <div className={`w-[35%] h-full relative flex items-center justify-center flex-shrink-0 opacity-[0.08] text-slate-900 pointer-events-none select-none transition-all duration-300 group-hover:opacity-80 group-hover:scale-110 ${textColorClass}`}>
              {illustration}
            </div>
          )}
          {!illustration && icon && !imageSrc && (
            <div className={`w-[35%] h-full relative flex items-center justify-center flex-shrink-0 opacity-[0.08] text-slate-900 pointer-events-none select-none transition-all duration-300 group-hover:opacity-80 group-hover:scale-110 ${textColorClass}`}>
              {cloneElement(icon, { className: "w-10 h-10" })}
            </div>
          )}
        </div>
        
        {/* Expanded chart/detail section — only shown when isExpanded is true */}
        {isExpanded && children && (
          <div className="px-4 pb-4 border-t border-slate-50 pt-3 h-[140px] z-10 relative bg-white">
            {children}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'dashboard') {
    const borderClasses = topColor
      ? `border-t-[5px] ${topColor} border-x border-b border-slate-200/80`
      : 'border border-slate-200/80 hover:border-slate-300/90';

    return (
      <div
        className={`bg-white ${borderClasses} rounded-xl transition-all duration-300 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 shadow-[0_8px_30px_rgba(0,0,0,0.03)] min-h-[54px] flex relative group overflow-hidden ${onClick ? 'cursor-pointer' : ''} ${className}`}
        onClick={onClick}
      >
        {!topColor && (
          <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-slate-200/60 to-transparent" />
        )}

        <div className={`p-5 pt-6 flex ${isExpanded ? 'flex-col justify-between flex-1 border-r border-slate-50' : 'items-center gap-4 w-full'}`}>
          {icon && (
            <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/50 flex items-center justify-center text-slate-700 shadow-sm group-hover:scale-105 group-hover:bg-slate-900 group-hover:text-white group-hover:border-slate-900 transition-all duration-300 flex-shrink-0">
              {cloneElement(icon, { size: 16 })}
            </div>
          )}
          <div className={`${isExpanded ? 'mt-4' : 'flex flex-col min-w-0'}`}>
            <div className="font-normal text-slate-900 leading-tight tracking-tight font-sans text-2xl group-hover:text-slate-950 transition-colors">
              {value}
            </div>
            <p className="text-[9px] font-normal text-slate-400 tracking-widest uppercase mt-1 leading-none truncate">{title}</p>
          </div>
        </div>

        {isExpanded && children && (
          <div className="flex-1 p-3 flex items-center justify-center bg-white">
            {children}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'minimal') {
    return (
      <div
        className={`p-4 flex flex-col justify-center transition-all duration-300 border border-slate-200/80 rounded-xl hover:shadow-[0_8px_20px_rgba(0,0,0,0.04)] bg-white relative overflow-hidden ${onClick ? 'cursor-pointer' : ''} ${className}`}
        onClick={onClick}
      >
        <div className="text-[14px] font-normal text-slate-500 mb-1.5 transition-colors">
          {title}
        </div>
        <div className="text-[16px] font-normal text-slate-800 leading-none mt-0.5">
          {value}
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    const borderClasses = topColor
      ? `border-t-[4px] ${topColor} border-x border-b border-slate-200/80`
      : 'border border-slate-200/80 hover:border-slate-300/90';

    return (
      <div
        className={`p-3 pt-4 flex items-center gap-3 transition-all duration-300 group h-[54px] ${borderClasses} rounded-xl hover:shadow-[0_12px_30px_rgba(0,0,0,0.06)] bg-white relative overflow-hidden ${onClick ? 'cursor-pointer' : ''} ${className}`}
        onClick={onClick}
      >
        {icon && (
          <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-200/50 flex items-center justify-center text-slate-700 shadow-sm group-hover:scale-105 group-hover:bg-slate-900 group-hover:text-white group-hover:border-slate-900 transition-all duration-300 flex-shrink-0">
            {cloneElement(icon, { size: 14 })}
          </div>
        )}
        <div className="overflow-hidden flex flex-col justify-center min-w-0">
          <div className="flex items-baseline gap-1.5 overflow-hidden">
            <div className="font-normal text-[13px] text-slate-900 font-sans leading-none">
              {value}
            </div>
          </div>
          <div className="text-[9px] font-normal text-slate-400 tracking-widest uppercase truncate mt-1 leading-none">{title}</div>
        </div>
      </div>
    );
  }

  const borderClasses = topColor
    ? `border-t-[5px] ${topColor} border-x border-b border-slate-200/80`
    : 'border border-slate-200/80 hover:border-slate-300/90';

  return (
    <div
      className={`bg-white ${borderClasses} rounded-xl transition-all duration-300 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 shadow-[0_8px_30px_rgba(0,0,0,0.03)] relative group overflow-hidden ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
    >
      {!topColor && (
        <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-slate-200/60 to-transparent" />
      )}

      {/* Always-visible top row: icon + value + title */}
      <div className="p-5 pt-6 flex items-center gap-4">
        {icon && (
          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/50 flex items-center justify-center text-slate-700 shadow-sm group-hover:scale-105 group-hover:bg-slate-900 group-hover:text-white group-hover:border-slate-900 transition-all duration-300 flex-shrink-0">
            {cloneElement(icon, { size: 16 })}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-normal text-slate-900 leading-tight tracking-wide font-sans text-2xl group-hover:text-slate-950 transition-colors">
              {value}
            </span>
            {trend && (
              <span className={`text-[11px] font-normal ${trend.value >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {trend.value >= 0 ? '↑' : '↓'}{Math.abs(trend.value)}%
              </span>
            )}
          </div>
          <p className="text-[9px] font-normal text-slate-400 tracking-widest uppercase mt-1 leading-none truncate">{title}</p>
          {trend?.label && <p className="text-[11px] text-slate-400 mt-1">{trend.label}</p>}
        </div>
      </div>

      {/* Expanded chart/detail section — only shown when isExpanded is true */}
      {isExpanded && children && (
        <div className="px-4 pb-4 border-t border-slate-50 pt-3 h-[140px]">
          {children}
        </div>
      )}
    </div>
  );
}
